import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import {
  API,
  authConfig,
  alertError,
  thStyle,
  tdStyle,
  pageTitleStyle,
  pageSubtitleStyle,
  ticketAssigneeLabel,
  ticketCategoryLabel,
  ticketClientBranchLabel,
  parseTicketListResponse,
  TICKET_LIST_PAGE_SIZE,
} from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import TicketStatusBadge from '../components/TicketStatusBadge';
import TicketDomainBadge from '../components/TicketDomainBadge';
import ListSearch from '../../../components/ListSearch';
import TicketStatusTabs, {
  statusTabToQuery,
  queryToStatusTab,
  TICKET_STATUS_TABS,
} from '../components/TicketStatusTabs';
import { TicketDetailLink } from '../ticketNav';
import { useProductFilter } from '../ProductFilterContext';

function TicketMyTicketsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { productId, selectedProduct } = useProductFilter();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [statusTab, setStatusTab] = useState(() => queryToStatusTab(searchParams));

  useEffect(() => {
    setStatusTab(queryToStatusTab(searchParams));
  }, [searchParams]);

  const fetchTickets = useCallback(async (tab, pageNum, q) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/my-tickets`, authConfig({
        ...statusTabToQuery(tab),
        ...(productId ? { product_id: productId } : {}),
        ...(q && q.trim() ? { search: q.trim() } : {}),
        limit: TICKET_LIST_PAGE_SIZE,
        skip: (pageNum - 1) * TICKET_LIST_PAGE_SIZE,
      }));
      const { items, total: count } = parseTicketListResponse(res);
      setTickets(items);
      setTotal(count);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load your tickets');
      setTickets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    const t = setTimeout(() => fetchTickets(statusTab, page, searchQuery), 300);
    return () => clearTimeout(t);
  }, [statusTab, page, searchQuery, fetchTickets]);

  useEffect(() => {
    setPage(1);
  }, [productId, statusTab, searchQuery]);

  const handleStatusTabChange = (tab) => {
    setStatusTab(tab);
    const next = new URLSearchParams(searchParams);
    next.delete('status_group');
    if (tab === 'all') next.delete('status');
    else next.set('status', tab);
    setSearchParams(next, { replace: true });
  };

  const tabLabel = TICKET_STATUS_TABS.find((t) => t.id === statusTab)?.label || 'My Tickets';
  const totalPages = Math.max(1, Math.ceil(total / TICKET_LIST_PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * TICKET_LIST_PAGE_SIZE + 1;
  const to = Math.min(page * TICKET_LIST_PAGE_SIZE, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  if (loading && tickets.length === 0) {
    return <p style={{ color: '#6B7280' }}>Loading your tickets…</p>;
  }

  return (
    <div>
      <h2 style={pageTitleStyle}>
        My Tickets{statusTab !== 'all' ? ` · ${tabLabel}` : ''}
        {selectedProduct ? ` · ${selectedProduct.name}` : ''}
      </h2>
      <p style={pageSubtitleStyle}>
        Tickets you created or that are assigned to you. Use the tabs to filter by status.
        {selectedProduct ? ` Showing ${selectedProduct.name} only.` : ''}
        {' '}
        {!loading ? (total === 0 ? '0 results' : `${from}–${to} of ${total}`) : ''}
      </p>

      {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}

      <TicketStatusTabs value={statusTab} onChange={handleStatusTabChange} />

      <ListSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search your tickets…"
        resultCount={tickets.length}
        totalCount={total}
        style={{ marginBottom: '6px' }}
        inputStyle={{ padding: '6px 28px 6px 32px', fontSize: '12px', borderRadius: '6px' }}
      />

      {total === 0 ? (
        <p style={{ color: '#6B7280', fontSize: '12px' }}>
          No tickets yet. <Link to="/dashboard/tickets/new" style={{ color: '#002FA7' }}>Create one</Link>
        </p>
      ) : (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '6px', opacity: loading ? 0.7 : 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                  <th style={thStyle}>Ticket No</th>
                  <th style={thStyle}>Domain</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Client / Branch</th>
                  <th style={thStyle}>Assignee</th>
                  <th style={thStyle}>Subject</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Priority</th>
                  <th style={thStyle}>Created (IST)</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.ticket_no} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={tdStyle}>
                      <TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontWeight: '600' }}>
                        {t.ticket_no}
                      </TicketDetailLink>
                    </td>
                    <td style={tdStyle}><TicketDomainBadge domain={t.domain} /></td>
                    <td style={tdStyle}>{ticketCategoryLabel(t)}</td>
                    <td style={{ ...tdStyle, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ticketClientBranchLabel(t)}>
                      {ticketClientBranchLabel(t)}
                    </td>
                    <td style={tdStyle}>{ticketAssigneeLabel(t)}</td>
                    <td style={{ ...tdStyle, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.subject}>{t.subject}</td>
                    <td style={tdStyle}><TicketStatusBadge status={t.status} /></td>
                    <td style={{ ...tdStyle, textTransform: 'capitalize' }}>{t.priority}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#4B5563' }}>{formatUtcToIst(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              Page {page} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="tk-btn tk-btn-secondary tk-btn-sm"
                disabled={!canPrev || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                <CaretLeft size={14} /> Prev
              </button>
              <button
                type="button"
                className="tk-btn tk-btn-secondary tk-btn-sm"
                disabled={!canNext || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <CaretRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TicketMyTicketsPage;
